import { ethers } from 'ethers';
import { StakingPool, UserPoolInfo } from 'api/StakingAPI';
import { calculateAPR, createAPRInputs, formatAPR } from 'utils/aprCalculation';

type ContractReadResult = {
  status: 'success' | 'failure';
  result?: unknown;
};

/**
 * Transforms contract read results into UserPoolInfo data
 */
export const transformUserPoolInfoFromContracts = (
  contractData: ContractReadResult[] | undefined,
  walletAddress: `0x${string}`,
  poolContractAddress: `0x${string}`,
  pool: StakingPool
): UserPoolInfo => {
  if (!contractData || !walletAddress || !poolContractAddress) {
    return getDefaultUserPoolInfo(walletAddress, poolContractAddress);
  }

  const [userInfo, claimable] = contractData;

  if (!userInfo || userInfo.status === 'failure') {
    return getDefaultUserPoolInfo(walletAddress, poolContractAddress);
  }

  // userInfo is a tuple: [amount, lockDate, unlockDate, score]
  const totalStaked = extractUserInfoValue(userInfo, 0);
  const lockDate = extractUserInfoValue(userInfo, 1);
  const unlockDate = extractUserInfoValue(userInfo, 2);
  const reputationScore = extractUserInfoValue(userInfo, 3);

  const claimableAmount = extractClaimableAmount(claimable);

  // Calculate APR
  const apr = calculateUserAPR(pool, reputationScore, totalStaked);

  return {
    walletAddress,
    contractAddress: poolContractAddress,
    totalStaked,
    lockDate,
    unlockDate,
    reputationScore,
    claimable: claimableAmount,
    apr,
  };
};

/**
 * Extracts a value from userInfo tuple result
 */
const extractUserInfoValue = (
  userInfo: ContractReadResult,
  index: number
): string => {
  if (
    userInfo.status === 'success' &&
    userInfo.result != null &&
    Array.isArray(userInfo.result)
  ) {
    try {
      return ethers.BigNumber.from(userInfo.result[index]).toString();
    } catch {
      return '0';
    }
  }
  return '0';
};

/**
 * Extracts claimable amount from contract result
 */
const extractClaimableAmount = (
  claimable: ContractReadResult | undefined
): string => {
  if (claimable && claimable.status === 'success' && claimable.result != null) {
    try {
      return ethers.BigNumber.from(claimable.result).toString();
    } catch {
      return '0';
    }
  }
  return '0';
};

/**
 * Calculates user APR based on pool data and user staking info
 */
const calculateUserAPR = (
  pool: StakingPool,
  reputationScore: string,
  totalStaked: string
): string => {
  if (!pool || pool.depositTokenSymbol === 'SDAO') {
    return '0';
  }

  try {
    const aprInputs = createAPRInputs(
      {
        totalRewards: pool.totalRewards,
        rewardsEmissionPeriod: pool.rewardsEmissionPeriod || '0',
        rewardsCommissionPercentage: pool.rewardsCommissionPercentage || '0',
        totalReputationScore: pool.totalReputationScore || '0',
      },
      {
        reputationScore: reputationScore,
        totalStaked: totalStaked,
      }
    );
    const result = calculateAPR(aprInputs);
    return formatAPR(result.apr);
  } catch {
    return '0';
  }
};

/**
 * Returns default user pool info in case of error or missing data
 */
const getDefaultUserPoolInfo = (
  walletAddress: `0x${string}`,
  poolContractAddress: `0x${string}`
): UserPoolInfo => {
  return {
    walletAddress,
    contractAddress: poolContractAddress,
    totalStaked: '0',
    lockDate: '0',
    unlockDate: '0',
    reputationScore: '0',
    claimable: '0',
    apr: '0',
  };
};
